import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../core/context/AuthContext";
import { submitConnectorRequest } from "../../connectorRequestService";
import "./ConnectorRequestModal.css";

/**
 * Modal form for requesting a coming-soon connector.
 *
 * @example
 * <ConnectorRequestModal
 *   connector={{ id: "slack", appName: "Slack" }}
 *   source="onboarding"
 *   onClose={() => setConnector(null)}
 * />
 */
const ConnectorRequestModal = ({ connector, source = "app", onClose }) => {
  const { user } = useAuth();
  const isOpen = Boolean(connector);
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setEmail(String(user?.email || "").trim());
    setNotes("");
    setError("");

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isSubmitting, onClose, user?.email]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is required.");
      return;
    }

    setIsSubmitting(true);
    try {
      await submitConnectorRequest({
        connector_id: connector.id,
        connector_name: connector.appName,
        email: trimmedEmail,
        notes: notes.trim(),
        source,
      });
      onClose?.();
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const modal = (
    <div className="connector-request-modal__backdrop" onClick={handleClose}>
      <div
        className="connector-request-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connector-request-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="connector-request-modal__close"
          onClick={handleClose}
          aria-label="Close connector request form"
        >
          ×
        </button>

        <div className="connector-request-modal__header">
          <h2 id="connector-request-title">Request {connector.appName}</h2>
          <p>
            Tell us you want this connector and we&apos;ll email you when it&apos;s
            available.
          </p>
        </div>

        <form className="connector-request-modal__form" onSubmit={handleSubmit}>
          <label className="connector-request-modal__field">
            <span>Connector</span>
            <input
              type="text"
              className="connector-request-modal__input"
              value={connector.appName}
              readOnly
            />
          </label>

          <label className="connector-request-modal__field">
            <span>
              Email <span className="connector-request-modal__required">*</span>
            </span>
            <input
              type="email"
              className="connector-request-modal__input"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </label>

          <label className="connector-request-modal__field">
            <span>Anything else we should know?</span>
            <textarea
              className="connector-request-modal__textarea"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Optional context for the team"
              rows={4}
            />
          </label>

          {error ? <p className="connector-request-modal__error">{error}</p> : null}

          <button
            type="submit"
            className="connector-request-modal__submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting…" : "Submit request"}
          </button>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ConnectorRequestModal;
